import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, PlayCircle } from "lucide-react";

const TYPE_PILL = "font-mono-cl text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 rounded-full bg-black/55 text-white border border-white/20 backdrop-blur";
// header images are auto-fitted; keep the subject's upper third visible
const IMG_POS = { objectPosition: "center 22%" };

function CardImage({ a }) {
  const img = a.profile_image || a.big_picture;
  if (img) return <img src={img} alt={a.name} style={IMG_POS} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />;
  return (
    <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-zinc-700 to-zinc-950">
      <span className="font-display text-6xl font-black text-white/25">{(a.name || "?")[0]}</span>
    </div>
  );
}

function FeaturedCard({ a }) {
  const primary = (a.types && a.types[0]) || "Markets";
  return (
    <Link to={`/analyst/${a.slug}`} data-testid={`spotlight-featured-${a.slug}`}
      className="relative rounded-[22px] overflow-hidden group block aspect-[4/5] sm:aspect-[16/11] lg:aspect-auto lg:h-full lg:min-h-[320px] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.55)]">
      <CardImage a={a} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/10" />
      <div className="absolute top-4 left-4 flex flex-wrap gap-1.5">
        {(a.types || []).slice(0, 2).map((t) => <span key={t} className={TYPE_PILL}>{t}</span>)}
      </div>
      {a.intro_video_url && (
        <div className="absolute top-4 right-4 flex items-center gap-1 text-[10px] font-mono-cl uppercase tracking-[0.12em] text-white bg-black/55 border border-white/20 rounded-full px-2 py-1 backdrop-blur">
          <PlayCircle className="w-3.5 h-3.5 text-[#ccff00]" /> Intro
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 p-5 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-2xl font-black text-white leading-none truncate">{a.name}</p>
          <p className="text-white/70 text-sm mt-1 truncate">{primary} Desk</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-display text-3xl font-black text-[#ccff00] leading-none">{a.published ?? 0}</p>
          <p className="font-mono-cl text-[10px] tracking-[0.18em] uppercase text-white/70 mt-1">Published</p>
        </div>
      </div>
    </Link>
  );
}

function VerticalCard({ a }) {
  const primary = (a.types && a.types[0]) || "Markets";
  return (
    <Link to={`/analyst/${a.slug}`} data-testid={`spotlight-vertical-${a.slug}`}
      className="relative rounded-[22px] overflow-hidden group block aspect-[3/4] lg:aspect-auto lg:h-full lg:min-h-[320px] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.55)]">
      <CardImage a={a} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
      <p className="absolute top-3 left-2 right-2 text-center font-display text-sm font-bold text-white drop-shadow truncate">{(a.name || "").split(" ")[0]}</p>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
        <span className="block font-display text-lg font-black text-white tracking-wide whitespace-nowrap [writing-mode:vertical-rl] rotate-180">{primary}</span>
      </div>
    </Link>
  );
}

export function AnalystSpotlight({ analysts }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const cats = useMemo(() => {
    const s = new Set();
    analysts.forEach((a) => (a.types || []).forEach((t) => s.add(t)));
    return ["All categories", ...Array.from(s)];
  }, [analysts]);

  if (!analysts || analysts.length === 0) return null;
  const [featured, ...rest] = analysts;
  const verticals = rest.slice(0, 2);

  const go = () => {
    const match = analysts.find((a) => (a.types || []).some((t) => t.toLowerCase().includes(q.toLowerCase())) || a.name.toLowerCase().includes(q.toLowerCase()));
    if (q && match) navigate(`/analyst/${match.slug}`);
    else navigate("/analysts");
  };

  return (
    <section className="px-4 sm:px-6 pt-6 pb-10" data-testid="analyst-spotlight">
      <div className="relative max-w-[1400px] mx-auto rounded-[28px] sm:rounded-[32px] overflow-hidden"
        style={{ background: "linear-gradient(120deg,#efece3 0%,#e9ebf0 45%,#e3e7ef 100%)" }}>
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(#000 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
        <div className="relative px-5 sm:px-10 py-8 sm:py-10">
          <div className="grid gap-4 lg:grid-cols-[1.05fr_1.35fr_0.62fr_0.62fr] lg:items-stretch">
            {/* Heading + search */}
            <div className="flex flex-col justify-between">
              <div>
                <p className="font-mono-cl text-[11px] tracking-[0.28em] uppercase text-zinc-500 mb-3 sm:mb-4">The Desk</p>
                <h2 className="font-display font-black text-[#0b0b0c] leading-[0.92] tracking-tight text-5xl sm:text-6xl">
                  Watch.<br />Learn.<br />Trade.
                </h2>
              </div>
              <div className="mt-6 lg:mt-8">
                <div className="flex items-center gap-2 bg-white rounded-full p-1.5 pl-5 shadow-[0_8px_30px_-10px_rgba(0,0,0,0.25)] max-w-full sm:max-w-sm">
                  <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()} placeholder="Find your analyst"
                    data-testid="spotlight-search" className="flex-1 bg-transparent text-sm text-zinc-800 placeholder:text-zinc-400 outline-none min-w-0" />
                  <button onClick={go} data-testid="spotlight-go"
                    className="shrink-0 grid place-items-center w-11 h-11 rounded-full bg-[#ccff00] text-black hover:brightness-105 active:scale-95 transition-[filter,transform]">
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Featured */}
            <FeaturedCard a={featured} />

            {/* Verticals: 2-col on mobile, individual grid items on desktop */}
            {verticals.length > 0 && (
              <div className="grid grid-cols-2 gap-4 lg:contents">
                {verticals.map((a) => <VerticalCard key={a.slug} a={a} />)}
              </div>
            )}
          </div>

          {/* Footer line + categories */}
          <div className="mt-8 sm:mt-10 text-center">
            <p className="font-display text-xl sm:text-3xl font-black text-[#0b0b0c]">
              Unlimited access to {analysts.length}+ analyst partners.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-4">
              {cats.map((c, i) => (
                <Link key={c} to={i === 0 ? "/analysts" : `/analysts/category/${encodeURIComponent(c)}`} data-testid={`spotlight-cat-${i}`}
                  className={`text-sm transition-colors ${i === 0 ? "text-[#0b0b0c] font-semibold underline underline-offset-4" : "text-zinc-500 hover:text-[#0b0b0c]"}`}>
                  {c}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
